import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";

import type { BackgroundItemView } from "@/lib/backgroundDomains";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { FieldError, joinDescriptionIds } from "@/ui/FieldError";
import { IconButton } from "@/ui/IconButton";
import { Field, Input, Textarea } from "@/ui/Input";
import { Notice } from "@/ui/Notice";
import { Tooltip } from "@/ui/Tooltip";

/**
 * A BG domain's ordered prompt rows: the text a clinician types, in the order
 * the interview asks it (#859).
 *
 * ## THE REFERENCE'S SHAPE, WITH THE PRODUCT'S IDENTITY
 *
 * The reference domain editor
 * (`/library/bg-sets/domain/family_childhood`) renders one card per step: a
 * drag handle, the prompt in an inline textarea, a delete control at the end,
 * and `Add prompt` under the list. The text is edited IN PLACE — there is no
 * Open/Back round trip, because a step IS its prompt.
 *
 * Two explicit additions, both from the S7 contract rather than from taste:
 *
 *   * **The item id is on the row, read-only.** The reference's rows are
 *     anonymous prose. A real BG item's `id` is the stable key the tracker
 *     stores, the conditional rules resolve and the safety nets gate on
 *     (`background_domains.ItemId`), and the ticket asks for "source domain IDs
 *     as stable read-only identity with clinical display names". A clinician
 *     reordering prompts has to be able to tell which record each row IS,
 *     because the rules that reference it are edited on the same rows by #913.
 *     Read-only AFTER it exists — the id a NEW prompt is created with is typed
 *     on the add control below (#977), because MOD's own ids cannot be
 *     generated and are what every rule in its source documents names.
 *   * **What the row carries beyond its prompt.** Base canon's
 *     `parents_background` holds a 900-character extraction note and eight
 *     quality examples. #859 could only NAME them, because nothing published
 *     their shape; #913 authors them through the `itemExtension` slot below, so
 *     the sentence that used to say "preserved unchanged when this prompt is
 *     saved" left with the screen that could not show them.
 *
 * ## REORDER IS BUTTONS, WITH `PathwayStepList`'s FOCUS AND ANNOUNCEMENT RULES
 *
 * The reference paints a drag handle. Two buttons are operable by keyboard,
 * pointer and assistive technology on the first day, which is #455's
 * requirement and the reason every reorder control in this application is a
 * pair — so the handle is not reproduced, and the behaviour AFTER the press is
 * `PathwayStepList`'s, restated here for the same two defects it documents:
 * focus moves to the pair's remaining operable half when the pressed control
 * becomes inert, and a polite live region says where the row landed.
 *
 * Not merged with `PathwayStepList` deliberately, and this is the same
 * reasoning that list gives for not merging with `NamedRowList`: it renders
 * rows whose subject is a stored PART and offers Open; these rows edit a field
 * INSIDE one structured document and have no second screen. Rewriting the
 * component the Pathway surface depends on, from a ticket scoped to BG, is how
 * a scoped change becomes an unreviewable one. The shared reorder behaviour is
 * a follow-up named in this pull request rather than left to be discovered.
 */

export interface BackgroundItemRowsProps {
  items: readonly BackgroundItemView[];
  /** The language being typed into, for the textarea's direction and label. */
  language: string | null;
  readOnly: boolean;
  readOnlyReason: string | null;
  onChangeDescription: (itemId: string, value: string) => void;
  /** Move the item to an absolute position in the list. */
  onMove: (itemId: string, to: number) => void;
  onRemove: (itemId: string) => void;
  add?: {
    label: string;
    /** Why adding is refused, or `null`. Never an unexplained disabled button. */
    refusal: string | null;
    /**
     * The id the box starts with — `nextItemId`'s generated `step_N` — which the
     * author replaces when they are rebuilding a source domain (#977).
     */
    suggestedId: string;
    /**
     * What the author has typed, or `null` while the field follows
     * `suggestedId`. The page owns this buffer so its Discard/version boundary
     * is the same one as every other transient value on the surface.
     */
    typedId: string | null;
    /**
     * Why THIS id may not be created, as a sentence, or `null`.
     *
     * The verdict is `lib/backgroundDomains.newItemIdRefusal`'s and the wording
     * is the page's: the grammar belongs to the service, the document belongs to
     * the draft, and neither is this component's to know.
     */
    idRefusal: (itemId: string) => string | null;
    onChangeTypedId: (itemId: string) => void;
    onAdd: (itemId: string) => void;
  };
  /**
   * Per-item authoring this ticket does not own, rendered inside the row it
   * belongs to (#913, #914).
   *
   * THE STABLE EXPANSION SLOT the ticket requires: "stable per-item expansion
   * slots consumed by the separate conditional-rule UI", and the contract's
   * "#913 adds the conditional/extraction/quality controls to the same rows,
   * not another editor". Keyed by the item's own stable `id` rather than by
   * position, so a reorder cannot move a rule editor onto a different item, and
   * rendered UNDER the prompt inside the same `<li>` so the two are one row to
   * a screen reader as well as visually.
   *
   * A render prop rather than a component: what #913 mounts needs the draft,
   * the refusal state and the mutation callbacks this component has no business
   * holding. Returning `null` — today's product, where no such editor exists —
   * emits nothing at all, so there is no empty container taking a margin.
   */
  itemExtension?: (item: BackgroundItemView) => ReactNode;
}

export function BackgroundItemRows({
  items,
  language,
  readOnly,
  readOnlyReason,
  onChangeDescription,
  onMove,
  onRemove,
  add,
  itemExtension,
}: BackgroundItemRowsProps) {
  // WHICH control should hold focus once the moved order has rendered, keyed by
  // the row's stable item ID rather than by its index — the index is exactly
  // what changed. A ref rather than state: nothing renders from it, so state
  // would mean a second render per move whose only job is to forget it.
  const restore = useRef<{ itemId: string; direction: "up" | "down" } | null>(
    null,
  );
  const [announcement, setAnnouncement] = useState("");
  const controls = useRef(new Map<string, HTMLButtonElement>());

  useEffect(() => {
    const pending = restore.current;
    if (pending === null) {
      return;
    }
    restore.current = null;
    const index = items.findIndex((item) => item.id === pending.itemId);
    if (index === -1) {
      // The row left the list between the move and this effect — somebody
      // else's version landed and re-seeded the draft. Guessing a neighbour
      // would move focus somewhere the clinician did not ask for.
      return;
    }
    const stillOperable =
      pending.direction === "up" ? index > 0 : index < items.length - 1;
    if (!stillOperable) {
      const opposite = pending.direction === "up" ? "down" : "up";
      controls.current.get(`${pending.itemId}:${opposite}`)?.focus();
    }
  }, [items]);

  function move(item: BackgroundItemView, index: number, direction: "up" | "down") {
    const to = direction === "up" ? index - 1 : index + 1;
    restore.current = { itemId: item.id, direction };
    setAnnouncement(
      `${item.id} moved to position ${to + 1} of ${items.length}.`,
    );
    onMove(item.id, to);
  }

  return (
    <div className="space-y-3">
      {/* Mounted unconditionally and empty until a move happens: a live region
          added to the DOM at the same moment it gains text is frequently not
          announced at all. */}
      <p aria-live="polite" className="sr-only">
        {announcement}
      </p>

      {items.length === 0 ? (
        <p className="rounded-md border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
          This domain has no prompts yet. A domain the runtime can open needs at
          least one.
        </p>
      ) : (
        <ol aria-label="Prompts, in interview order" className="space-y-3">
          {items.map((item, index) => (
            <BackgroundItemRow
              key={item.id}
              item={item}
              index={index}
              total={items.length}
              language={language}
              readOnly={readOnly}
              readOnlyReason={readOnlyReason}
              onChangeDescription={onChangeDescription}
              onMove={(direction) => move(item, index, direction)}
              onRemove={() => onRemove(item.id)}
              registerControl={(direction, element) => {
                const key = `${item.id}:${direction}`;
                if (element) controls.current.set(key, element);
                else controls.current.delete(key);
              }}
              {...(itemExtension ? { extension: itemExtension(item) } : {})}
            />
          ))}
        </ol>
      )}

      {readOnly || add === undefined ? null : <AddBackgroundItem {...add} />}
    </div>
  );
}

/**
 * `Add prompt`, with the id the new item will permanently carry (#977).
 *
 * ## WHY THE ID IS ON THIS CONTROL AND NOWHERE ELSE
 *
 * #859 generated `step_N` and offered no control, on the reasoning that an item
 * id is the stable key the tracker, the conditional rules and the safety nets
 * resolve against and the ratified contract forbids renaming a clinical id.
 * That reasoning survives intact for RENAMING — the row above still renders the
 * id as read-only text and there is no control that changes one.
 *
 * It does not survive for CREATION. MOD's six source domains hold 35 item ids,
 * 15 of which no base document supplies and every one of which is named by a
 * `conditional_trigger_rules` reference; a prompt whose id cannot be typed is a
 * rule that cannot be rebuilt (#872, S7 contract §1 "clinical IDs", §4 "stable
 * string `id`"). So the id is chosen exactly once, here, by the person creating
 * the record.
 *
 * ## THE BOX IS PRE-FILLED, WHICH IS WHAT KEEPS THE OLD FLOW
 *
 * `suggestedId` is the same `step_N` #859 generated, so a clinician who has no
 * opinion about ids still presses one button and gets one prompt. What changed
 * is that the identity being minted is VISIBLE before it is minted rather than
 * appearing on the row afterwards, permanent and unexplained.
 *
 * The typed value is held by the PAGE with `null` meaning "untouched", so the
 * box FOLLOWS the suggestion as rows are added and removed, and stops following
 * the moment the author types. The page is the owner because it also owns
 * Discard and the pinned version: text staged for one draft must not survive
 * either boundary. Deriving the visible value rather than syncing it in an
 * effect is `NewBackgroundDomainDialog`'s rule from #859's own review.
 */
function AddBackgroundItem({
  label,
  refusal,
  suggestedId,
  typedId,
  idRefusal,
  onChangeTypedId,
  onAdd,
}: NonNullable<BackgroundItemRowsProps["add"]>) {
  const errorId = useId();
  const itemId = typedId ?? suggestedId;
  // The document's verdict on what is in the box RIGHT NOW. Recomputed rather
  // than remembered: the draft changes underneath this control every time a row
  // is added or removed, and a remembered verdict would outlive its document.
  //
  // NOT ASKED AT ALL while the control is refused outright. The two overlap on
  // exactly the case that matters — an unread grammar refuses the control AND
  // every candidate — and rendering both is one fact stated twice in two alerts,
  // the duplicate-message defect this page states its own rule about.
  const idProblem = refusal === null ? idRefusal(itemId) : null;
  const blocked = refusal !== null || idProblem !== null;

  function submit() {
    if (blocked) {
      return;
    }
    onAdd(itemId);
  }

  return (
    // UNDER the rows, where the reference puts `Add prompt` — unlike the
    // Main-concern detail, whose add control is a header action. The two differ
    // in the reference and `NamedRowList` already records why.
    <div className="space-y-2 rounded-md border border-dashed border-border p-3">
      {/* A REFUSAL IN A TOOLTIP IS NOT A REPORTED FAILURE — the rule this page
          states for its blank-prompt and broken-reference notices. The tooltip
          answers someone who hovers the refused button; "the service did not
          tell us which IDs it accepts" is a failure of a read, and it needs
          somewhere it is simply visible. */}
      {refusal === null ? null : <Notice role="alert">{refusal}</Notice>}
      <div className="flex flex-col gap-2 @lg:flex-row @lg:items-start">
        <div className="min-w-0 flex-1">
          <Field
            label="New prompt ID"
            description="The permanent identity of the prompt being added: what the tracker stores its answer under, and what a condition or safety net names to reach it. It cannot be changed afterwards."
          >
            {({ inputId, describedBy }) => (
              <>
                <Input
                  id={inputId}
                  dir="ltr"
                  value={itemId}
                  aria-invalid={idProblem !== null || undefined}
                  aria-describedby={joinDescriptionIds(
                    describedBy,
                    idProblem === null ? undefined : errorId,
                  )}
                  onChange={(event) => onChangeTypedId(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      // The rows live inside the page's own layout rather than a
                      // form, so Enter has no default to prevent and no submit to
                      // reach; this is the whole keyboard path to adding a prompt.
                      event.preventDefault();
                      submit();
                    }
                  }}
                />
                {idProblem === null ? null : (
                  <FieldError id={errorId} message={idProblem} />
                )}
              </>
            )}
          </Field>
        </div>
        <Tooltip content={refusal ?? idProblem ?? label}>
          <Button
            variant="outline"
            size="sm"
            className="@lg:mt-7"
            disabled={blocked}
            onClick={submit}
          >
            <Plus aria-hidden="true" className="h-4 w-4" />
            {label}
          </Button>
        </Tooltip>
      </div>
    </div>
  );
}

function BackgroundItemRow({
  item,
  index,
  total,
  language,
  readOnly,
  readOnlyReason,
  onChangeDescription,
  onMove,
  onRemove,
  registerControl,
  extension,
}: {
  item: BackgroundItemView;
  index: number;
  total: number;
  language: string | null;
  readOnly: boolean;
  readOnlyReason: string | null;
  onChangeDescription: (itemId: string, value: string) => void;
  onMove: (direction: "up" | "down") => void;
  onRemove: () => void;
  registerControl: (
    direction: "up" | "down",
    element: HTMLButtonElement | null,
  ) => void;
  extension?: ReactNode;
}) {
  const promptId = useId();

  return (
    <li className="rounded-md border border-border bg-card px-3 py-2.5">
      <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
        <span className="w-6 shrink-0 pt-1.5 text-xs tabular-nums text-muted-foreground">
          {index + 1}.
        </span>

        <div className="min-w-0 grow basis-64 space-y-1.5">
          {/* THE LABEL IS THE STABLE ID, and it is a real `<label>` for the
              textarea rather than decoration: a page of eight prompt boxes
              named only "Prompt" is the repeated-ambiguous-control defect the
              parity audit already recorded once. `dir="ltr"` because an id is
              a machine identifier in Latin script sitting beside Hebrew prose,
              and the surrounding RTL context would otherwise reorder its
              underscores. */}
          <label
            htmlFor={promptId}
            className="flex flex-wrap items-center gap-2 text-xs"
          >
            <code
              dir="ltr"
              className="rounded bg-muted px-1 py-0.5 font-mono text-foreground"
            >
              {item.id}
            </code>
            {item.type ? (
              <>
                {/* A REAL TEXT NODE between the two, not the flex `gap`:
                    accessible names are built from text content, so the id and
                    the badge concatenated into "routine_is_fixedrequired" — one
                    word, announced as one word, and unmatchable by any name
                    query. The gap keeps them apart visually; this keeps them
                    apart in the name. */}{" "}
                <Badge tone="outline">{item.type}</Badge>
              </>
            ) : null}
          </label>

          <Textarea
            id={promptId}
            rows={2}
            value={item.description}
            readOnly={readOnly}
            // The stored clinical text is Hebrew on both MOD and NII, and the
            // browser's own bidirectional algorithm is what gets this right for
            // a mixed string — `auto` reads the first strong character rather
            // than this component asserting a direction the content may not
            // have.
            dir="auto"
            {...(language ? { lang: language } : {})}
            onChange={(event) =>
              onChangeDescription(item.id, event.currentTarget.value)
            }
          />
        </div>

        {readOnly ? null : (
          <div className="flex items-center gap-0.5 pt-1">
            {/* THE ITEM'S OWN ID IS IN EVERY LABEL, and the POSITION is
                deliberately not: it changes on every move, and a name that
                changes under a focused control is re-announced as a different
                button. */}
            <IconButton
              ref={(element) => registerControl("up", element)}
              label={`Move ${item.id} up`}
              icon={<ArrowUp className="h-4 w-4" />}
              disabled={index === 0}
              onClick={() => onMove("up")}
            />
            <IconButton
              ref={(element) => registerControl("down", element)}
              label={`Move ${item.id} down`}
              icon={<ArrowDown className="h-4 w-4" />}
              disabled={index === total - 1}
              onClick={() => onMove("down")}
            />
            <Tooltip content={readOnlyReason ?? `Remove ${item.id}`}>
              <IconButton
                label={`Remove ${item.id}`}
                icon={<Trash2 className="h-4 w-4" />}
                onClick={onRemove}
              />
            </Tooltip>
          </div>
        )}
      </div>

      {/* #913's slot, INSIDE this row. `basis-full` is not needed: the row is
          the list item, so an extension simply follows the flex line. */}
      {extension ?? null}
    </li>
  );
}
